/**
 * Channel Stats Policy (TV1-2361)
 *
 * Loads the two per-channel figures the channels LIST shows but the integrations
 * readmodel does not carry: category-mapping coverage and publication count.
 *
 * ── Why each is affordable ────────────────────────────────────────────────
 *
 * CATEGORIES — one call, not one per channel. The route is
 * `/systems-integrations/channel-categories/:integrationId?` and the param is
 * OPTIONAL, so omitting it returns every channel-category row for the account
 * (already tenant-scoped by `FilterByAccountIdTransformer`). Grouping by
 * `integrationId` client-side is cheaper than N round trips and stays correct as
 * channels are added.
 *
 * PUBLICATIONS — one count query per channel, issued in parallel. There is no
 * group-by-connector aggregation on the publications readmodel, and adding one
 * would be a projection change; an account has a handful of channels, so N small
 * count queries is the proportionate answer. `limit: 1` because only `count` is
 * read — the page of rows is thrown away.
 *
 * ── Refresh ───────────────────────────────────────────────────────────────
 *
 * Reacts to the channel SET changing (a channel connected or removed), not to
 * every channel edit — the figures do not move when someone renames a store. The
 * import progress on the card comes from the batch-processes subscription, which
 * is live, so a running import updates without this policy re-running.
 */

import { Policy } from '@commandDrivenStateEngine/core/policies';
import { ClientLibraryType } from '@bett3r-dev/teselly-client-library';
import { ChannelsState } from '../state';
import { ChannelsCommand } from '../commands';
import {
  WAREHOUSE_RULE_DEFINITION_ID_SET,
  LOGISTICS_RULE_DEFINITION_ID_SET
} from '../../../salesChannelSetup/state/state';

type Stats = Record<string, Partial<{
  mappedCategories: number;
  totalCategories: number;
  publications: number | null;
  setup: { inventory: boolean; billing: boolean; logistics: boolean };
}>>;

type Dispatch = ( cmd: ChannelsCommand, meta: { source: string }) => void;

const loadCategoryCoverage = async (
  backend: ClientLibraryType,
  dispatch: Dispatch
): Promise<void> => {
  try {
    // No `integrationId` — the whole account's rows in one trip.
    const rows = await backend.queries.SystemsIntegrationsChannelCategories({} as any );
    const stats: Stats = {};

    for ( const row of ( rows ?? []) as Array<{ integrationId?: string; isMapped?: boolean }> ) {
      const id = row.integrationId;
      if ( !id ) continue;
      const entry = stats[id] ?? { mappedCategories: 0, totalCategories: 0 };
      entry.totalCategories = ( entry.totalCategories ?? 0 ) + 1;
      if ( row.isMapped ) entry.mappedCategories = ( entry.mappedCategories ?? 0 ) + 1;
      stats[id] = entry;
    }

    dispatch( { type: 'integrations/channels/stats/set', payload: { stats }}, { source: 'api' });
  } catch ( error ) {
    // Non-fatal: the card falls back to showing no coverage rather than blocking
    // the list. A channel with no figures still renders its name and its import.
    console.error( '[channels] Failed to load channel category coverage:', error );
  }
};

const loadPublicationCounts = async (
  backend: ClientLibraryType,
  channelIds: Array<{ id: string; connectorId: string; integrationUserId: string }>,
  dispatch: Dispatch
): Promise<void> => {
  const results = await Promise.all(
    channelIds.map( async ( channel ) => {
      try {
        const result = await backend.queries.ItemsPublishingPublicationsSearch({
          limit: 1,
          offset: 0,
          filter: [
            { field: 'connectorId', op: 'eq', value: channel.connectorId },
            { field: 'integrationUserId', op: 'eq', value: channel.integrationUserId }
          ]
        } as any );
        return { id: channel.id, publications: ( result?.count ?? 0 ) as number };
      } catch {
        // One channel failing must not take the others' figures with it.
        return { id: channel.id, publications: null };
      }
    })
  );

  const stats: Stats = {};
  for ( const r of results ) {
    stats[r.id] = { publications: r.publications };
  }

  dispatch( { type: 'integrations/channels/stats/set', payload: { stats }}, { source: 'api' });
};

/**
 * Per-channel setup coverage (TV1-2367).
 *
 * ── One query, not one per channel ────────────────────────────────────────
 *
 * The setup wizard's init policy asks for the rules of ONE channel
 * (`hierarchyEntityIds contains <channelId>`). The list needs every channel's,
 * so it asks for all of the account's rules once and groups client-side — the
 * same trick the category coverage above uses, and for the same reason.
 *
 * ── Why not read `channelSetupCompletedAt` ────────────────────────────────
 *
 * Because it lies. The wizard wrote it on EXIT, configured or not, so a channel
 * with no warehouse rule could sit on the grid claiming to be set up while every
 * order it took went nowhere. A rule either exists or it does not.
 *
 * Billing is the exception and is not a rule: the entity is associated onto the
 * integration itself, so it is read from the channel row.
 */
const loadSetupCoverage = async (
  backend: ClientLibraryType,
  channels: Array<{ id: string; billingEntityId?: string | null }>,
  dispatch: Dispatch
): Promise<void> => {
  const results = await Promise.all(
    channels.map( async ( channel ) => {
      try {
        /*
         * The SAME filtered call the setup panel's init policy makes, once per
         * channel and in parallel.
         *
         * An unfiltered `Rules({})` grouped client-side was tried first and is
         * the obvious optimisation, but it is a different call shape against a
         * base route whose behaviour without a filter is not what the rest of
         * the app relies on — and when it returned nothing, `setup` stayed
         * undefined and the card fell back to the completion marker, which lies.
         * The result was the exact bug this policy exists to prevent: a channel
         * missing two of three concerns rendering as fully configured.
         *
         * A handful of channels makes N small queries the proportionate answer,
         * and it reuses a call that demonstrably works.
         */
        const rules = ( await backend.queries.Rules({
          filter: [{ field: 'hierarchyEntityIds', op: 'contains', value: channel.id }]
        } as any )) as Array<{ ruleDefinitionId?: string }> | null;

        return {
          id: channel.id,
          setup: {
            inventory: ( rules ?? []).some(( r ) =>
              r.ruleDefinitionId && WAREHOUSE_RULE_DEFINITION_ID_SET.has( r.ruleDefinitionId )),
            logistics: ( rules ?? []).some(( r ) =>
              r.ruleDefinitionId && LOGISTICS_RULE_DEFINITION_ID_SET.has( r.ruleDefinitionId )),
            billing: channel.billingEntityId != null
          }
        };
      } catch ( error ) {
        console.error( `[channels] Failed to load setup coverage for ${ channel.id }:`, error );
        // One channel failing must not take the others' figures with it, and it
        // must NOT be reported as configured — see `deriveChannelCardState`.
        return { id: channel.id, setup: undefined };
      }
    })
  );

  const stats: Stats = {};
  for ( const r of results ) {
    if ( r.setup ) stats[r.id] = { setup: r.setup };
  }
  if ( Object.keys( stats ).length === 0 ) return;

  dispatch( { type: 'integrations/channels/stats/set', payload: { stats }}, { source: 'api' });
};

const channelIdentities = ( state: ChannelsState ) =>
  Object.values( state.salesChannels ).map(( c ) => ({
    id: c.id,
    connectorId: c.connectorId,
    integrationUserId: c.integrationUserId,
    billingEntityId: ( c as { billingEntityId?: string | null }).billingEntityId ?? null
  }));

export const createChannelStatsPolicy = (
  backend: ClientLibraryType
): Policy<ChannelsState, ChannelsCommand> => {
  const load = ( state: ChannelsState, dispatch: Dispatch ) => {
    const channels = channelIdentities( state );
    if ( channels.length === 0 ) return;
    void loadCategoryCoverage( backend, dispatch );
    void loadPublicationCounts( backend, channels, dispatch );
    void loadSetupCoverage( backend, channels, dispatch );
  };

  return {
    name: 'channels-stats',

    init: ({ getState, dispatch }) => {
      load( getState(), dispatch as Dispatch );
    },

    // Only when the channel SET changes — renames and setting edits do not move
    // these figures, and re-running on every channel mutation would issue N+1
    // queries for a label change.
    shouldReact: ( prev, next ) =>
      Object.keys( next.salesChannels ).length !== Object.keys( prev.salesChannels ).length,

    onStateChange: ({ next, ctx }) => {
      load( next, ctx.dispatch as Dispatch );
    }
  };
};
